/* eslint @typescript-eslint/strict-boolean-expressions: off */
import styles from "./OnboardingQuestions.module.css";
import { Question, Questions, questions } from "./questions";
import {
  RadioButtonGroup,
  InputRadioButton,
} from "src/components/ui-components/Input/InputRadioButton";
import { useState } from "react";
import { Button } from "src/components/ui-components/Button/Button";
import { SelectBox } from "src/components/ui-components/Input/SelectBox";
import { useSubmitOnboardingQsMutation } from "src/gql/codegen/graphql";
import { Session } from "src/utils/users/types";
import { IconWarning } from "src/components/ui-components/Icons/IconWarning";
import { useAnalytics } from "src/components/analytics/analytics";

interface SelectedResponses {
  [key: string]: string;
}

export function Content(props: {
  session: Session;
  userId: number;
  onQsSubmitted: () => void;
}) {
  const { session, userId, onQsSubmitted } = props;
  const { captureEvent } = useAnalytics();

  const [selected, setSelected] = useState<SelectedResponses>({});

  const questionsData: Questions = questions;

  const [submitOnboardingQsMutation, submitOnboardingQsMutationRes] =
    useSubmitOnboardingQsMutation({
      context: { session },
      variables: {
        user_id: userId,
        brings_to_dune: selected["brings_to_dune"] ?? "",
        achieve_with_dune: selected["achieve_with_dune"] ?? "",
        sql_experience: selected["sql_experience"],
        blockchain_experience: selected["blockchain_experience"] ?? "",
        organization_size: selected["organization_size"],
        version: questionsData.version,
        until: new Date(7258118400000), // Wed, Jan 01 2200 00:00:00 UTC
      },
      onCompleted: onQsSubmitted,
    });

  const onSelectResponse = (questionKey: string, response: string) => {
    setSelected((prevState) => ({
      ...prevState,
      [questionKey]: response,
    }));
  };

  const hasError = submitOnboardingQsMutationRes.error;

  return (
    <div className={styles.menu}>
      <div className={styles.content}>
        <div className={styles.questions}>
          <p>Help us customize your onboarding:</p>
          {questionsData.questions.map(
            (q: Question) =>
              // question about organization size and sql experience are conditional based on previous answers
              ((q.question_key !== "organization_size" &&
                q.question_key !== "sql_experience") ||
                (q.question_key === "sql_experience" &&
                  selected["achieve_with_dune"] ===
                    "create_queries_and_dashboards") ||
                (q.question_key === "organization_size" &&
                  selected["brings_to_dune"] === "job")) && (
                <div className={styles.question} key={q.question_key}>
                  <h3>{q.question_text}</h3>
                  {q.response.metadata.inputType === "radio" ? (
                    <RadioButtonGroup
                      name={q.question_key}
                      value={selected[q.question_key] || ""}
                      onChange={(value) =>
                        onSelectResponse(q.question_key, value)
                      }
                    >
                      {q.response.metadata.values.map((option) => (
                        <InputRadioButton
                          key={option.key}
                          value={option.key}
                          theme="green"
                        >
                          {option.value}
                        </InputRadioButton>
                      ))}
                    </RadioButtonGroup>
                  ) : (
                    <SelectBox
                      name={q.question_key}
                      size="M"
                      type="contained"
                      placeholder={q.placeholder || "Select an option..."}
                      onChange={(event) =>
                        onSelectResponse(q.question_key, event.target.value)
                      }
                      value={selected[q.question_key] || ""}
                    >
                      {q.response.metadata.values.map((option) => (
                        <option value={option.key} key={option.key}>
                          {option.value}
                        </option>
                      ))}
                    </SelectBox>
                  )}
                </div>
              )
          )}
        </div>
        {hasError && (
          <div className={styles.error}>
            <IconWarning />
            Something went wrong. Please try again.
          </div>
        )}

        <div className={styles.actions}>
          <Button
            theme="green"
            size="M"
            onClick={() => {
              captureEvent("OnboardingQuestions: submitted", {
                user_id: session.user?.id,
              });
              submitOnboardingQsMutation();
            }}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
